import { SerializedData, Store } from "bernie-core";
import { action, observable } from "mobx";
import { Template, FlexBffTemplateResponse, ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { ModuleDefinition } from "src/typings/flexFramework/FlexComponent";
import { NewBlossomComponent } from "components/flexFramework/domain/NewBlossomComponent";
import { Logger } from "bernie-logger";
import { BreadcrumbsFlexModuleResult } from "src/typings/microserviceModels/breadcrumbs-flex-module";
import { FlexModuleStore } from "src/stores/FlexModuleStore";

export class TemplateStore extends Store {
  @observable
  public template: Template;

  @observable
  public blossomComponentTree: NewBlossomComponent[];

  public breadcrumbsModel: BreadcrumbsFlexModuleResult;
  private blossomComponentsLogger: Logger;

  public constructor(state: SerializedData, logger: Logger, blossomComponentsLogger: Logger) {
    super(state, logger);
    this.blossomComponentsLogger = blossomComponentsLogger;
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  /**
   * initializes TemplateStore with data from other stores. Called from controller
   * @param  {FlexBffTemplateResponse} bffResponse
   * @param  {ExtendedContextStore} context
   * @param  {FlexModuleStore} flexModuleStore
   * @returns void
   */
  @action
  public setup(bffResponse: FlexBffTemplateResponse, context: ExtendedContextStore, flexModuleStore: FlexModuleStore) {
    if (bffResponse) {
      this.template = bffResponse.template;

      this.blossomComponentTree = this.template.regions.reduce((result, child) => {
        const childBlossomComponent = this.moduleDefinitionToBlossomComponent(child, flexModuleStore, context);
        if (childBlossomComponent !== undefined) {
          result.push(childBlossomComponent);
        }

        return result;
      }, [] as NewBlossomComponent[]);
    }
  }

  /**
   * creates NewBlossomComponent equivalent of provided ModuleDefintion
   * @param  {ModuleDefinition} moduleDefinition
   * @param  {FlexModuleStore} flexModuleStore
   * @param  {ExtendedContextStore} context
   * @returns NewBlossomComponent|undefined
   */
  private moduleDefinitionToBlossomComponent(
    moduleDefinition: ModuleDefinition,
    flexModuleStore: FlexModuleStore,
    context: ExtendedContextStore
  ): NewBlossomComponent | undefined {
    // create model object from all non-standard moduleDefinition properites
    const model = { ...moduleDefinition };
    delete model.id;
    delete model.name;
    delete model.fmId;
    delete model.fmTitleId;
    delete model.view;
    delete model.ajaxOnly;
    delete model.clientSideOnly;
    delete model.scene;
    delete model.modules;
    delete model.groups;
    delete model.children;

    // normalize name
    let name = "";
    if (moduleDefinition.fmType === "REGION") {
      name = "region";
      model.name = moduleDefinition.name;
    } else if (moduleDefinition.fmType === "TAB") {
      name = "tab";
      model.name = moduleDefinition.name;
    } else {
      name = moduleDefinition.name;
    }

    // gather and create decendant BlossomComponents
    const childModuleDefinitions: ModuleDefinition[] = [
      ...(moduleDefinition.modules || []),
      ...(moduleDefinition.groups || []),
      ...(moduleDefinition.children || []),
    ];
    const children = childModuleDefinitions.reduce((result, child) => {
      const childBlossomComponent = this.moduleDefinitionToBlossomComponent(child, flexModuleStore, context);
      if (childBlossomComponent !== undefined) {
        result.push(childBlossomComponent);
      }

      return result;
    }, [] as NewBlossomComponent[]);

    return new NewBlossomComponent(
      {
        id: moduleDefinition.id,
        name,
        fmId: moduleDefinition.fmId,
        fmTitleId: moduleDefinition.fmTitleId,
        view: moduleDefinition.view,
        ajaxOnly: moduleDefinition.ajaxOnly,
        clientSideOnly: moduleDefinition.clientSideOnly,
        scene: moduleDefinition.scene,
        children,
        model,
      },
      context,
      flexModuleStore,
      this.blossomComponentsLogger
    );
  }

  /**
   * removes data that will be re-constructed client-side
   * @returns SerializedData
   */
  public toJSON(): SerializedData {
    return super.toJSON("blossomComponentsLogger");
  }
}
